import React, { useState, useContext } from 'react';
import PresetDndContext from '../common/DnD/PresetDndContext';
import {
  horizontalListSortingStrategy,
  SortableContext,
} from '@dnd-kit/sortable';
import SortableItem from '../common/DnD/SortableItem';
import { handleSortUpdate } from '../common/DnD/sortUpdater';
import Icon from '../common/Icon';
import Modal from '../common/Modal';
import AddFilterField from './AddFilterField';
import { notify } from '../../helpers/notifications';
import {
  createFilterField,
  destroyFilterField,
} from './constants/handleFilterFields';
import { adjustSortablesAfterDelete, dragSort } from '../../helpers/sorters';
import { FormContext } from '../../hooks/Contexts';

const FilterFields = ({ filterFields, setFilterFields, userCanEdit }) => {
  const { form, fieldsets } = useContext(FormContext);
  const [addingFilterField, setAddingFilterField] = useState(false);
  const ffIds = filterFields.map((ff) => ff.field_id);

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      let sort = filterFields.map((ff) => ff.id).indexOf(over.id);
      let backup = [...filterFields];
      setFilterFields((ffs) => dragSort(ffs, active, over));
      let sorted = await handleSortUpdate('filter_fields', active.id, sort);
      if (!sorted.ok) setFilterFields(backup);
    }
  };

  const onCreateSuccess = (filterField) => {
    setFilterFields((ffs) => {
      return [...ffs, filterField];
    });
    setAddingFilterField(false);
  };

  const handleModal = () => {
    if (filterFields.length === 8) {
      return notify.alertWarning(
        'Notice',
        'Maxmimum number of filter fields reached.'
      );
    }
    setAddingFilterField(true);
  };

  const handleAddFilterField = async (fieldID, e) => {
    if (!fieldID) return;
    let ff = {
      form_id: form.id,
      field_id: fieldID,
      sort: filterFields.length,
    };

    let created = await createFilterField(ff);
    if (created.ok) {
      onCreateSuccess(created.data.filter_field);
    }
  };

  const handleDestroy = async (ff) => {
    await destroyFilterField(ff, (response) => {
      if (response.ok)
        setFilterFields(adjustSortablesAfterDelete(filterFields, ff.id));
    });
  };

  // Non-draggable, non-editable columns (ID, Submitted)
  const LockedField = ({ columnName }) => (
    <li className='ui-sortable-handle locked'>
      <div className='cargo'>
        <div className='name' title={columnName}>
          {columnName}
        </div>
      </div>
    </li>
  );

  // Draggable, editable filter fields
  const FilterField = ({ ff }) => (
    <div className='cargo'>
      <div className='name'>{ff.field.name}</div>
      <span>
        {userCanEdit && (
          <Icon
            className='destroy'
            onClick={() => {
              handleDestroy(ff);
            }}
            icon='xmark'
          />
        )}
      </span>
    </div>
  );

  return (
    <>
      <section className='filter-fields'>
        <h2>
          Default Filter Fields ({filterFields.length}/8)
          {userCanEdit && (
            <Icon
              role='button'
              title='Add filterable field'
              onClick={() => handleModal()}
              icon='plus fa-border'
            />
          )}
        </h2>

        <Modal
          show={addingFilterField}
          onClose={() => setAddingFilterField(false)}
          title='filter-field-creation-modal'
        >
          <AddFilterField
            fieldsets={fieldsets}
            handleAddFilterField={handleAddFilterField}
            ffIds={ffIds}
            filtersFull={filterFields.length === 8}
          />
        </Modal>

        <div className='details'>
          <PresetDndContext handleDragEnd={handleDragEnd}>
            <ol className='orderable ui-sortable'>
              <SortableContext
                strategy={horizontalListSortingStrategy}
                items={filterFields}
                disabled={!userCanEdit}
              >
                <LockedField columnName={'ID'} />
                {filterFields ? (
                  <>
                    {filterFields.slice(0, 4).map((ff) => {
                      return (
                        <SortableItem
                          key={ff.id}
                          id={ff.id}
                          className='ui-sortable-handle'
                          title={ff.field.name}
                          disabled={!userCanEdit}
                        >
                          <FilterField ff={ff} />
                        </SortableItem>
                      );
                    })}
                    {filterFields.length > 4 && (
                      <>
                        <li
                          className='divider'
                          title={'<- Always visible | Expanded filters ->'}
                        >
                          <div className='cargo'>
                            <div className='name'>
                              <i className='fa-solid fa-left-right' />
                            </div>
                          </div>
                        </li>
                        {filterFields.slice(4).map((ff) => {
                          return (
                            <SortableItem
                              key={ff.id}
                              id={ff.id}
                              className='ui-sortable-handle'
                              title={ff.field.name}
                              disabled={!userCanEdit}
                            >
                              <FilterField ff={ff} />
                            </SortableItem>
                          );
                        })}
                      </>
                    )}
                  </>
                ) : (
                  <li className='empty'>
                    Doesn't look like you've added any filters...
                  </li>
                )}
                <LockedField columnName={'Submitted'} />
              </SortableContext>
            </ol>
          </PresetDndContext>
        </div>
      </section>
    </>
  );
};

export default FilterFields;
